import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useCallback } from "react";
import {
  registerSendCode,
  registerVerifyCode,
  loginSendCode,
  loginVerifyCode,
  logoutUser as logoutUserThunk,
  deleteAccount as deleteAccountThunk,
  getUserProfile,
} from "./auth.thunk";
import { logout, resetCodeSent, clearError } from "./auth.slice";
import { isError, isIdle, isLoading, isSuccess } from "@/app/utils/asyncStatus";
import {
  RegisterRequest,
  LoginSendCodeRequest,
  LoginVerifyCodeRequest,
} from "./auth.types";

/**
 * useAuth altı ayrı selector kurar (data, error, status, codeSent,
 * isAuthenticated, userProfile). Sadece "giriş yapılmış mı" sorusunu soran
 * bileşenler için tek primitif selector yeterli — değer karşılaştırması yapıldığı
 * için auth slice'ının başka alanları değiştiğinde render tetiklenmez.
 */
export const useIsAuthenticated = () =>
  useAppSelector((s) => s.auth.isAuthenticated);

const useAuth = () => {
  const dispatch = useAppDispatch();
  const authData = useAppSelector((s) => s.auth.data);
  const authError = useAppSelector((s) => s.auth.error);
  const authStatus = useAppSelector((s) => s.auth.status);
  const codeSent = useAppSelector((s) => s.auth.codeSent);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const userProfile = useAppSelector((s) => s.auth.userProfile);

  const sendRegistrationCode = useCallback(
    async (data: RegisterRequest) => {
      const result = await dispatch(registerSendCode(data));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const verifyRegistrationCode = useCallback(
    async (data: RegisterRequest) => {
      const result = await dispatch(registerVerifyCode(data));
      const success = result.meta.requestStatus === "fulfilled";

      if (success) {
        // Fire in background — don't block redirect on profile fetch
        dispatch(getUserProfile());
      }

      return success;
    },
    [dispatch],
  );

  const sendLoginCode = useCallback(
    async (data: LoginSendCodeRequest) => {
      const result = await dispatch(loginSendCode(data));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const verifyLoginCode = useCallback(
    async (data: LoginVerifyCodeRequest) => {
      const result = await dispatch(loginVerifyCode(data));
      const success = result.meta.requestStatus === "fulfilled";

      if (success) {
        // Fire in background — don't block redirect on profile fetch
        dispatch(getUserProfile());
      }

      return success;
    },
    [dispatch],
  );

  const logoutUser = useCallback(async () => {
    await dispatch(logoutUserThunk());
    // fulfilled veya rejected — her iki durumda da slice token'ları siliyor
    // ve isAuthenticated=false yapıyor. Caller her zaman redirect yapmalı.
    return true;
  }, [dispatch]);

  const logoutLocal = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const deleteAccount = useCallback(async () => {
    const result = await dispatch(deleteAccountThunk());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  const resetCodeSentStatus = useCallback(() => {
    dispatch(resetCodeSent());
  }, [dispatch]);

  const fetchUserProfile = useCallback(async () => {
    const result = await dispatch(getUserProfile());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    authData,
    isAuthenticated,
    codeSent,
    authError,
    authStatus,
    userProfile,
    isIdle: isIdle(authStatus),
    isLoading: isLoading(authStatus),
    isSuccess: isSuccess(authStatus),
    isError: isError(authStatus),
    sendRegistrationCode,
    verifyRegistrationCode,
    sendLoginCode,
    verifyLoginCode,
    logoutUser,
    logoutLocal,
    deleteAccount,
    resetCodeSentStatus,
    fetchUserProfile,
    clearAuthError,
  };
};

export default useAuth;
